import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { cloneDeep, isEqual } from 'lodash-es';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Header } from '@components/header';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconDirectionLeft } from '@components/icons/direction-left';
import { IconTrash } from '@components/icons/trash';
import { RelativeSpinner } from '@components/loading-spinner';
import { PageContent } from '@components/page-containers/page-container';
import {
  ColumnLeftSidebar,
  ColumnMiddleWithLeftSidebar,
  Grid,
} from '@components/page-containers/page-containers';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { ToggleGroup } from '@components/toggle-group';
import { useEvent } from '@hooks/use-event';
import { CareerSiteLanguageType } from '@locale/locale-store';
import { useScopedTranslate } from '@locale/translate';
import { getKeys } from '@utils/objects';

import { useGetApplicationForm, useGetJob, usePatchApplicationForm } from 'api/jobs/hooks';
import { ApplicationFormFields, LanguageData } from 'api/jobs/requests';
import { Spacer } from 'components/Spacer';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';
import { Job, JobQuestionMultiLanguage } from 'types/jobs';

import { DraggableQuestion, QuestionCard } from './draggable-question';

import { getMissingJobPostingTranslations } from '../helpers';
import { useJobLanguages } from '../language-box';
import { useQuestionsidebar } from '../questions/question-type-sidebar';
import { Actions } from '../top-bar-actions';
import { useUnsavedChangesPrompt } from '../unsaved-changes-modal';

export type SegmentedButtonType = 'off' | 'optional' | 'required';

export type DraggingState = {
  id: number;
  order: number;
  index: number;
} | null;

const getDetail = (required: boolean, include: boolean) => {
  if (required) return 'required';
  if (include) return 'optional';
  return 'off';
};

export const ApplicationForm = () => {
  const { jobId } = useParams();
  const applicationFormQuery = useGetApplicationForm(Number(jobId), jobId !== undefined);
  const jobQuery = useGetJob(Number(jobId), jobId !== undefined);
  if (
    (applicationFormQuery.isLoading && applicationFormQuery.fetchStatus !== 'idle') ||
    (jobQuery.isLoading && jobQuery.fetchStatus !== 'idle')
  ) {
    return <RelativeSpinner />;
  }
  if (!applicationFormQuery.data || !jobQuery.data) {
    return null;
  }
  if (applicationFormQuery.isSuccess) {
    return (
      <RenderApplicationForm
        job={jobQuery.data}
        questions={applicationFormQuery.data.questions}
        details={applicationFormQuery.data.details}
        jobLanguages={applicationFormQuery.data.languages}
      />
    );
  }
  return <RelativeSpinner />;
};

export const RenderApplicationForm = (props: {
  questions: JobQuestionMultiLanguage[];
  details: ApplicationFormFields;
  jobLanguages: LanguageData[];
  job: Job;
}) => {
  const { questions, details, jobLanguages, job } = props;
  const { t } = useScopedTranslate(ApplicationFormLocale);
  const { current } = useStore();
  const { showModal } = useModal();
  const navigate = useNavigate();
  const editApplicationForm = usePatchApplicationForm();
  const showQuestionSidebar = useQuestionsidebar();

  const [draggingItem, setDraggingItem] = useState<DraggingState>(null);
  const [ssn, setSsn] = useState<SegmentedButtonType>(
    details && getDetail(details.requireSsn, details.includeSsn),
  );
  const [phone, setPhone] = useState<SegmentedButtonType>(
    details && getDetail(details.requirePhone, details.includePhone),
  );
  const [cv, setCv] = useState<SegmentedButtonType>(
    details && getDetail(details.requireCv, details.includeCv),
  );
  const [coverLetter, setCoverLetter] = useState<SegmentedButtonType>(
    details && getDetail(details.requireCoverLetter, details.includeCoverLetter),
  );
  const jobLanguagesCode = jobLanguages.map((lang) => lang.code);
  const {
    languages,
    languageStatus,
    noLangEnabled,
    isDirtyLanguages,
    resetLanguages,
    LanguageBox,
    languageBoxProps,
    onAddLanguage,
  } = useJobLanguages(job, jobLanguagesCode);

  const [jobQuestions, setJobQuestions] = useState<JobQuestionMultiLanguage[]>(
    cloneDeep(questions),
  );
  const originalQuestions = useRef<JobQuestionMultiLanguage[]>(cloneDeep(questions));

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  const draggingQuestion = draggingItem ? jobQuestions[draggingItem.index] : null;
  const questionIds = jobQuestions.map((question) => String(question.id));

  function onDragStart(event: DragStartEvent) {
    const { active } = event;
    const order = active.data.current?.order;
    setDraggingItem({
      order: order,
      index: active.data.current?.sortable.index,
      id: active.data.current?.id,
    });
  }

  const onDragEnd = async (event: DragEndEvent) => {
    if (!draggingItem) return;
    const { active, over } = event;
    setDraggingItem(null);
    if (!over || active.id === over.id) return;
    const overIndex = questionIds.indexOf(over.id.toString());
    const activeIndex = questionIds.indexOf(active.id.toString());
    changeQuestionOrder(activeIndex, overIndex);
  };

  const onDragCancel = () => {
    setDraggingItem(null);
  };

  const addLanguage = (code: CareerSiteLanguageType) => {
    const newJobQuestions = cloneDeep(jobQuestions);
    newJobQuestions.forEach((question) => {
      question.title = { ...question.title, [code]: '' };
      if (question.type === 'dropdown' || 'multidropdown') {
        question.options?.forEach((option) => {
          option.display = { ...option.display, [code]: '' };
        });
      }
    });
    setJobQuestions(newJobQuestions);
    onAddLanguage(code);
  };

  const changeQuestionOrder = (oldIndex: number, newIndex: number) => {
    const newJobQuestions = [...jobQuestions];
    const [removed] = newJobQuestions.splice(oldIndex, 1);
    newJobQuestions.splice(newIndex, 0, removed);
    setJobQuestions(newJobQuestions);
    newJobQuestions.forEach((question, index) => {
      question.order = index;
    });
  };

  const removeQuestion = (index: number) => {
    const hasApplicants = jobLanguages.some((language) => language.hasApplicants === true);

    showModal('CONFIRM_MODAL', {
      confirm: () => {
        const newJobQuestions = [...jobQuestions];
        newJobQuestions.splice(index, 1);
        setJobQuestions(newJobQuestions);
        return true;
      },
      data: {
        id: index,
      },
      buttonIcon: <IconTrash />,
      headerText: t('removeQuestion'),
      mainText: hasApplicants
        ? t('removeQuestionConfirmationLarge')
        : t('removeQuestionConfirmationSmall'),
      confirmText: t('remove'),
      negative: true,
      dangerText: hasApplicants ? t('canNotBeUndone') : undefined,
      hideCheckbox: !hasApplicants,
    });
  };

  const addQuestion = (question: JobQuestionMultiLanguage) => {
    const newJobQuestions = [...jobQuestions];
    question.order = newJobQuestions.length;
    newJobQuestions.push(question);
    setJobQuestions(newJobQuestions);
  };

  const editQuestion = (index: number, newQuestion: JobQuestionMultiLanguage) => {
    const newJobQuestions = [...jobQuestions];
    newJobQuestions[index] = newQuestion;
    setJobQuestions(newJobQuestions);
  };

  const prepareNewDetails = () => {
    const newDetails = {
      ...(details.requireSsn !== (ssn === 'required') && { requireSsn: ssn === 'required' }),
      ...(details.includeSsn === false &&
        (ssn === 'required' || ssn === 'optional') && { includeSsn: true }),
      ...(details.includeSsn !== false && ssn === 'off' && { includeSsn: false }),
      ...(details.requireCoverLetter !== (coverLetter === 'required') && {
        requireCoverLetter: coverLetter === 'required',
      }),
      ...(details.includeCoverLetter === false &&
        (coverLetter === 'required' || coverLetter === 'optional') && {
          includeCoverLetter: true,
        }),
      ...(details.includeCoverLetter !== false &&
        coverLetter === 'off' && {
          includeCoverLetter: false,
        }),
      ...(details.requireCv !== (cv === 'required') && {
        requireCv: cv === 'required',
      }),
      ...(details.includeCv === false &&
        (cv === 'required' || cv === 'optional') && {
          includeCv: true,
        }),
      ...(details.includeCv !== false &&
        cv === 'off' && {
          includeCv: false,
        }),
      ...(details.requirePhone !== (phone === 'required') && {
        requirePhone: phone === 'required',
      }),
      ...(details.includePhone === false &&
        (phone === 'required' || phone === 'optional') && {
          includePhone: true,
        }),
      ...(details.includePhone !== false &&
        phone === 'off' && {
          includePhone: false,
        }),
    };
    return newDetails;
  };

  const isDirtyDetails = () => {
    const currentDetails = prepareNewDetails();
    return getKeys(currentDetails).some((key) => currentDetails[key] !== details[key]);
  };
  const isDirtyQuestions = !isEqual(jobQuestions, originalQuestions.current);
  const isDirty = isDirtyDetails() || isDirtyQuestions || isDirtyLanguages;

  const prepareData = useEvent(() => {
    const newDetails = prepareNewDetails();
    const data = {
      ...(isDirtyDetails() && { details: newDetails }),
      ...(!isEqual(jobQuestions, originalQuestions.current) && {
        questions: jobQuestions,
      }),
      ...(!isEqual(languageStatus, jobLanguages) && {
        languages: languageStatus,
      }),
    };
    return data;
  });

  useUnsavedChangesPrompt({
    isDirty,
    onSave: async () => {
      const data = prepareData();
      await editApplicationForm.mutateAsync({ id: job.id, data: data });
    },
    isPublished: job.isPublished,
  });

  useEffect(() => {
    originalQuestions.current = cloneDeep(questions);
    setJobQuestions(cloneDeep(originalQuestions.current));
  }, [questions]);

  const missingTranslations: { [key in CareerSiteLanguageType]?: boolean } = {};
  languages.length > 1 &&
    languages.forEach((language) => {
      const missingTranslationsInLanguage = jobQuestions.some((question) => {
        if (!question.title[language]) {
          return true;
        }
        return question.options?.some((option) => !option.display[language]);
      });
      missingTranslations[language] = missingTranslationsInLanguage;
    });

  const missingTranslationsJobPosting = getMissingJobPostingTranslations(job, languages);

  const submit = async () => {
    const data = prepareData();
    if (!data || Object.keys(data).length === 0) {
      return;
    }
    await editApplicationForm.mutateAsync({ id: job.id, data: data });
  };

  const onSaveAndContinue = async () => {
    if (isDirty) {
      await submit();
      navigate(`/job/${job.id}/edit/access`, { state: 'override' });
    } else {
      navigate(`/job/${job.id}/edit/access`);
    }
  };

  const discardAllChanges = () => {
    resetLanguages();
    setJobQuestions(cloneDeep(originalQuestions.current));
    setSsn(getDetail(details.requireSsn, details.includeSsn));
    setCv(getDetail(details.requireCv, details.includeCv));
    setPhone(getDetail(details.requirePhone, details.includePhone));
    setCoverLetter(getDetail(details.requireCoverLetter, details.includeCoverLetter));
  };

  const clearJobQuestionLanguage = (code: CareerSiteLanguageType) => {
    const newJobQuestions = cloneDeep(jobQuestions);
    newJobQuestions.forEach((question) => {
      delete question.title[code];
      question.options?.forEach((option) => {
        delete option.display[code];
      });
    });
    setJobQuestions(newJobQuestions);
  };

  const newLanguageBoxprops = {
    ...languageBoxProps,
    missingTranslations: missingTranslations,
    missingTranslationsElsewhere: missingTranslationsJobPosting,
    onAdd: addLanguage,
    discardJobQuestions: clearJobQuestionLanguage,
  };
  return (
    <>
      <Header
        breadcrumbs={<Breadcrumb items={[{ label: job.title }]} />}
        actions={
          <Actions
            onSave={submit}
            hasUnsavedChanges={isDirty}
            job={job}
            onCancel={discardAllChanges}
            noLangEnabled={noLangEnabled}
            dataCyPublish="publish-job"
            isSaving={editApplicationForm.isLoading}
          />
        }
      />
      <PageContent>
        <Grid>
          {current.langs.length > 1 && (
            <ColumnLeftSidebar>
              <LanguageBox {...newLanguageBoxprops} />
            </ColumnLeftSidebar>
          )}
          <ColumnMiddleWithLeftSidebar>
            <Stack space={6}>
              <Text font="heading2" text={t('pageTitle')} />
              <Separator />
              <Text font="bold18" text={t('detailsTitle')} />
              <Stack>
                <PersonalDetailsRow title={t('fullName')} isRequired space={16} />
                <PersonalDetailsRow title={t('email')} isRequired space={16} />
                <PersonalDetailsRow
                  title={t('ssn')}
                  selectedValue={ssn}
                  setSelected={setSsn}
                  dataCyPrefix="SSN"
                  space={8}
                />
                <PersonalDetailsRow
                  title={t('phone')}
                  selectedValue={phone}
                  setSelected={setPhone}
                  dataCyPrefix="Phone"
                  space={8}
                />
                <PersonalDetailsRow
                  title={t('cv')}
                  selectedValue={cv}
                  setSelected={setCv}
                  dataCyPrefix="CV"
                  space={8}
                />
                <PersonalDetailsRow
                  title={t('coverLetter')}
                  selectedValue={coverLetter}
                  setSelected={setCoverLetter}
                  dataCyPrefix="Cover-letter"
                  space={8}
                />
              </Stack>
              <Stack space={4}>
                <Stack space={2}>
                  <Text font="bold18" text={t('questions')} />
                  <Text font="regular14" color="purple80" text={t('questionsDescription')} />
                </Stack>
                {jobQuestions && (
                  <DndContext
                    sensors={sensors}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onDragCancel={onDragCancel}
                  >
                    <DragOverlay>
                      {draggingQuestion && (
                        <QuestionCard
                          question={draggingQuestion}
                          index={Number(draggingItem?.index)}
                          languages={languages}
                        />
                      )}
                    </DragOverlay>
                    <Stack space={2}>
                      <SortableContext items={questionIds} strategy={verticalListSortingStrategy}>
                        {jobQuestions.map((question, index) => (
                          <Stack space={2} key={question.id}>
                            <DraggableQuestion
                              question={question}
                              index={index}
                              languages={languages}
                              removeQuestion={removeQuestion}
                              editQuestion={(editedQuestion) => editQuestion(index, editedQuestion)}
                              isDragging={draggingQuestion?.id === question.id}
                            />
                          </Stack>
                        ))}
                      </SortableContext>
                    </Stack>
                  </DndContext>
                )}
                <Stack space={2} align="end">
                  <Button
                    iconLeft={<IconAddPlus />}
                    text={t('addQuestion')}
                    variant="secondary"
                    onClick={() => showQuestionSidebar({ languages: languages, addQuestion })}
                    dataCy="add-question-button"
                  />
                </Stack>
              </Stack>
              <Separator />
              {job.id && (
                <Stack space={2} orientation="horizontal" justify="space-between">
                  <Link to={`/job/${job.id}/edit/description`}>
                    <Button
                      iconLeft={<IconDirectionLeft />}
                      text={t('jobPosting')}
                      variant="secondary"
                    />
                  </Link>
                  <Button
                    text={
                      isDirty
                        ? job.isPublished
                          ? t('publishAndContinue')
                          : t('saveAndContinue')
                        : t('continue')
                    }
                    variant="primary-blue"
                    onClick={() => onSaveAndContinue()}
                    isDisabled={editApplicationForm.isLoading}
                    dataCy="save-continue-create-button"
                  />
                </Stack>
              )}
            </Stack>
          </ColumnMiddleWithLeftSidebar>
        </Grid>
      </PageContent>
    </>
  );
};

const PersonalDetailsRow = ({
  title,
  isRequired = false,
  selectedValue,
  setSelected,
  dataCyPrefix,
  space = 0,
}: {
  title: string;
  isRequired?: boolean;
  selectedValue?: SegmentedButtonType;
  setSelected?: (value: SegmentedButtonType) => void;
  dataCyPrefix?: string;
  space?: number;
}) => {
  const { t } = useScopedTranslate(ApplicationFormLocale);
  return (
    <>
      <Spacer top={space} bottom={space}>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Text font="regular14" text={title} />
          {isRequired && <Text font="regular14" text={t('alwaysRequired')} color="purple60" />}
          {selectedValue && setSelected && (
            <ApplicationFormButtonSegmented
              selected={selectedValue}
              setSelected={setSelected}
              dataCyPrefix={dataCyPrefix}
            />
          )}
        </Stack>
      </Spacer>
      <Separator />
    </>
  );
};

type SegmentedButtonsProps = {
  selected: SegmentedButtonType;
  setSelected: (value: SegmentedButtonType) => void;
  dataCyPrefix?: string;
};

export const ApplicationFormButtonSegmented = (props: SegmentedButtonsProps) => {
  const { selected, setSelected, dataCyPrefix } = props;
  const { t } = useScopedTranslate(ApplicationFormLocale);

  return (
    <ToggleGroup.Root value={selected} onChange={setSelected}>
      <ToggleGroup.Button
        value="off"
        text={t('off')}
        variant="purple"
        data-cy={`${dataCyPrefix}-off-button`}
      />
      <ToggleGroup.Button
        value="optional"
        text={t('optional')}
        variant="orange"
        data-cy={`${dataCyPrefix}-optional-button`}
      />
      <ToggleGroup.Button
        value="required"
        text={t('required')}
        variant="blue"
        data-cy={`${dataCyPrefix}-required-button`}
      />
    </ToggleGroup.Root>
  );
};

const ApplicationFormLocale = {
  en: {
    pageTitle: 'Application form',
    detailsTitle: 'Personal details',
    fullName: 'Full name',
    email: 'Email',
    alwaysRequired: 'Always required',
    ssn: 'SSN / ID Number',
    phone: 'Phone',
    cv: 'CV / Resumé',
    coverLetter: 'Cover letter',
    questions: 'Questions',
    questionsDescription:
      'Add custom screening questions to help find the best candidates. Focus on the most relevant questions to keep candidates motivated throughout the application process.',
    addQuestion: 'Add question',
    langNotPublished: 'Language not published',
    removeQuestion: 'Remove question',
    removeQuestionConfirmationSmall: 'Are you sure you want to remove this question?',
    removeQuestionConfirmationLarge:
      'Are you sure you want to remove this question?\nIf applicants have responses to these questions they will be removed.',
    remove: 'Remove',
    draft: 'Draft',
    unsavedChanges: 'Unsaved changes',
    published: 'Published',
    jobPosting: 'Job posting',
    access: 'Access',
    off: 'Off',
    optional: 'Optional',
    required: 'Required',
    saveAndContinue: 'Save & Continue',
    continue: 'Continue',
    canNotBeUndone: 'This can not be undone',
    publishAndContinue: 'Publish changes & Continue',
  },
  is: {
    pageTitle: 'Umsóknarform',
    detailsTitle: 'Persónulegar upplýsingar',
    fullName: 'Fullt nafn',
    email: 'Netfang',
    alwaysRequired: 'Alltaf krafist',
    ssn: 'Kennitala',
    phone: 'Símanúmer',
    cv: 'CV / Ferilskrá',
    coverLetter: 'Kynningarbréf',
    questions: 'Spurningar',
    questionsDescription:
      'Bættu við sérsniðnum spurningum sem hentar þessari starfsauglýsingu. Best er að hafa þær stuttar og eins hnitmiðaðar og mögulegt er.',
    addQuestion: 'Bæta við spurningu',
    langNotPublished: 'Tungumál ekki birt',
    removeQuestion: 'Fjarlægja spurningu',
    removeQuestionConfirmationSmall: 'Ertu viss um að þú viljir fjarlægja þessa spurningu?',
    removeQuestionConfirmationLarge:
      'Ertu viss um að þú viljir fjarlægja þessa spurningu?\nEf umsækjendur hafa svarað þessum spurningum verða þau svör fjarlægð.',
    remove: 'Fjarlægja',
    draft: 'Drög',
    unsavedChanges: 'Óvistaðar breytingar',
    published: 'Birt',
    jobPosting: 'Starfslýsing',
    access: 'Aðgangur',
    off: 'Óvirkt',
    optional: 'Valkvætt',
    required: 'Nauðsynlegt',
    saveAndContinue: 'Vista & Halda áfram',
    continue: 'Halda áfram',
    canNotBeUndone: 'Þetta er ekki hægt að afturkalla',
    publishAndContinue: 'Birta breytingar & Halda áfram',
  },
};
